import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import { Mail, Phone, Trash2, Eye, Reply } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

export const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = async () => {
    try {
      const response = await adminApi.getContacts();
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await adminApi.updateContactStatus(id, status);
      fetchContacts();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteContact(id);
      setSelectedContact(null);
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const classes = {
      new: 'badge-new',
      read: 'badge-read',
      replied: 'badge-replied',
    };
    const labels = {
      new: 'Yeni',
      read: 'Okundu',
      replied: 'Yanıtlandı',
    };
    return <span className={classes[status]}>{labels[status]}</span>;
  };

  return (
    <div data-testid="admin-contacts-page">
      <div className="mb-8">
        <h1
          className="text-2xl font-medium"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          İletişim Formları
        </h1>
        <p className="text-[#8e8e93] mt-1">
          Gelen iletişim taleplerini yönetin
        </p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-6 border border-[#d1d1d6] animate-pulse">
              <div className="h-4 bg-[#d1d1d6] w-48 mb-3"></div>
              <div className="h-3 bg-[#d1d1d6] w-32"></div>
            </div>
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <div className="bg-white p-12 border border-[#d1d1d6] text-center">
          <Mail className="w-12 h-12 text-[#d1d1d6] mx-auto mb-4" />
          <p className="text-[#8e8e93]">Henüz iletişim formu gönderimi yok.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Contact List */}
          <div className="space-y-4">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                data-testid={`contact-item-${contact.id}`}
                onClick={() => {
                  setSelectedContact(contact);
                  if (contact.status === 'new') {
                    handleStatusChange(contact.id, 'read');
                  }
                }}
                className={`bg-white p-6 border cursor-pointer transition-all ${
                  selectedContact?.id === contact.id
                    ? 'border-[#041f60]'
                    : 'border-[#d1d1d6] hover:border-[#041f60]/50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium">{contact.name}</h3>
                    <p className="text-sm text-[#8e8e93]">{contact.subject}</p>
                  </div>
                  {getStatusBadge(contact.status)}
                </div>
                <p className="text-sm text-[#8e8e93] line-clamp-2 mb-3">
                  {contact.message}
                </p>
                <p className="text-xs text-[#8e8e93]">
                  {formatDate(contact.created_at)}
                </p>
              </div>
            ))}
          </div>

          {/* Contact Detail */}
          {selectedContact && (
            <div
              data-testid="contact-detail"
              className="bg-white p-8 border border-[#d1d1d6] sticky top-8"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2
                    className="text-xl font-medium"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {selectedContact.name}
                  </h2>
                  <p className="text-[#8e8e93] text-sm mt-1">
                    {selectedContact.subject}
                  </p>
                </div>
                {getStatusBadge(selectedContact.status)}
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-[#041f60]" />
                  <a
                    href={`mailto:${selectedContact.email}`}
                    className="text-[#1a1a1a] hover:text-[#041f60]"
                  >
                    {selectedContact.email}
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-[#041f60]" />
                  <a
                    href={`tel:${selectedContact.phone}`}
                    className="text-[#1a1a1a] hover:text-[#041f60]"
                  >
                    {selectedContact.phone}
                  </a>
                </div>
              </div>

              <div className="border-t border-[#d1d1d6] pt-6 mb-6">
                <h4 className="font-medium mb-3">Mesaj</h4>
                <p className="text-[#8e8e93] whitespace-pre-wrap">
                  {selectedContact.message}
                </p>
              </div>

              <p className="text-xs text-[#8e8e93] mb-6">
                Gönderilme: {formatDate(selectedContact.created_at)}
              </p>

              <div className="flex gap-3">
                {selectedContact.status !== 'replied' && (
                  <Button
                    onClick={() => handleStatusChange(selectedContact.id, 'replied')}
                    data-testid="mark-replied"
                    className="flex-1 bg-[#041f60] hover:bg-[#b87333]"
                  >
                    <Reply size={18} className="mr-2" />
                    Yanıtlandı
                  </Button>
                )}

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="delete-contact"
                      className="border-red-500 text-red-500 hover:bg-red-50"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Mesajı Sil</AlertDialogTitle>
                      <AlertDialogDescription>
                        Bu mesajı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>İptal</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(selectedContact.id)}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        Sil
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
